import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty } from '../../components/ui';
import { Kpis, Panel, BarsH, Stack100 } from '../../components/charts';

/* ============================================================
   Dashboard qa.

   Angka yang dipimpin halaman ini bukan JUMLAH NCR melainkan
   berapa lama mereka terbuka, dan berapa banyak yang benar-benar
   sedang menahan uang. Sepuluh NCR yang ditutup dalam seminggu
   adalah proyek yang sehat; satu yang terbuka enam puluh hari
   bukan.
   ============================================================ */

export type Summary = {
  policy: { major: string; minor: string };
  itps: number;
  inspections: {
    total: number;
    outcomes: { PASS: number; PASS_WITH_COMMENT: number; FAIL: number; PENDING: number };
  };
  firstTimePass: { rate: number | null; first: number; passed: number };
  ncrs: {
    total: number;
    open: number;
    blocking: number;
    overdue: number;
    oldestOpenDays: number;
    ageing: Record<string, number>;
    byDiscipline: { code: string; count: number }[];
  };
  blockingList: {
    id: string;
    ncrNo: string;
    title: string;
    severity: string;
    activityCode: string | null;
  }[];
};

const POLICY_LABEL: Record<string, string> = {
  BLOCK: 'menahan sertifikasi',
  FLAG: 'menandai saja',
  IGNORE: 'tidak berpengaruh',
};

export function Dashboard({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Summary>(`/quality/summary?projectId=${projectId}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the quality summary.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function setPolicy(field: 'major' | 'minor', value: string) {
    setBusy(true);
    try {
      await api.put('/quality/policy', { projectId, [field]: value });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Gagal mengubah kebijakan.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const { ncrs, inspections, firstTimePass, policy } = data;
  const o = inspections.outcomes;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Quality Management</h1>
          <p>
            Gerbang mutu duduk di aktivitas yang sama yang membawa uang. Kalau mutu hidup di
            spreadsheet terpisah, ia tidak pernah menghentikan apa pun.
          </p>
        </div>
      </div>

      {data.itps === 0 && ncrs.total === 0 ? (
        <Empty
          title="Belum ada ITP maupun NCR"
          body="Susun ITP dan tempelkan ke aktivitas. Titik tahan yang belum lolos akan menghentikan pekerjaan, dan NCR Major yang terbuka akan menahan sertifikasi."
          action={
            <button className="btn btn-primary" onClick={() => nav('/itps')}>
              Buka ITP
            </button>
          }
        />
      ) : (
        <>
          <Kpis
            items={[
              { label: 'ITP', value: String(data.itps), note: `${inspections.total} inspeksi tercatat` },
              {
                label: 'Lolos pertama',
                value: firstTimePass.rate == null ? '—' : `${firstTimePass.rate.toFixed(0)}%`,
                note:
                  firstTimePass.rate == null
                    ? 'belum ada hasil inspeksi'
                    : `${firstTimePass.passed} dari ${firstTimePass.first} titik`,
                tone: firstTimePass.rate == null ? undefined : firstTimePass.rate >= 85 ? 'good' : 'warn',
              },
              {
                label: 'NCR terbuka',
                value: String(ncrs.open),
                note: ncrs.overdue > 0 ? `${ncrs.overdue} lewat tenggat` : undefined,
                tone: ncrs.overdue > 0 ? 'warn' : undefined,
              },
              {
                label: 'Menahan sertifikasi',
                value: String(ncrs.blocking),
                note: ncrs.blocking > 0 ? 'uang tertahan karena mutu' : 'tidak ada',
                tone: ncrs.blocking > 0 ? 'crit' : 'good',
              },
            ]}
          />

          <Panel
            title="Kebijakan gerbang"
            note="Memblokir pembayaran punya akibat komersial nyata, jadi keras-lunaknya adalah keputusan pemilik proyek — bukan keputusan perangkat lunak."
            wide
          >
            <div className="filters" style={{ marginBottom: 0 }}>
              <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                NCR Major
                <select
                  className="select"
                  value={policy.major}
                  disabled={busy}
                  onChange={(e) => setPolicy('major', e.target.value)}
                >
                  {Object.entries(POLICY_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                NCR Minor
                <select
                  className="select"
                  value={policy.minor}
                  disabled={busy}
                  onChange={(e) => setPolicy('minor', e.target.value)}
                >
                  {Object.entries(POLICY_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <span className="hint">Observasi tidak pernah menahan uang.</span>
            </div>
          </Panel>

          <div className="grid-2">
            <Panel title="Umur NCR terbuka" note="Bukan jumlahnya yang penting, melainkan berapa lama.">
              <BarsH
                rows={[
                  { label: '0 – 15 hari', value: ncrs.ageing['0-15'] ?? 0, tone: 'good' },
                  { label: '16 – 30 hari', value: ncrs.ageing['16-30'] ?? 0, tone: 'accent' },
                  { label: '31 – 60 hari', value: ncrs.ageing['31-60'] ?? 0, tone: 'warn' },
                  { label: 'Lebih dari 60 hari', value: ncrs.ageing['60+'] ?? 0, tone: 'crit' },
                ]}
              />
              {ncrs.oldestOpenDays > 0 && (
                <p className="panel-foot">Yang tertua terbuka {ncrs.oldestOpenDays} hari.</p>
              )}
            </Panel>

            <Panel title="Hasil inspeksi" note="Lolos dengan catatan tetap lolos: pekerjaannya diterima.">
              {inspections.total === 0 ? (
                <p className="hint">Belum ada inspeksi tercatat.</p>
              ) : (
                <Stack100
                  slices={[
                    { name: 'Lolos', value: o.PASS, color: 'var(--series-3)' },
                    { name: 'Lolos dengan catatan', value: o.PASS_WITH_COMMENT, color: 'var(--series-2)' },
                    { name: 'Gagal', value: o.FAIL, color: 'var(--status-critical)' },
                    { name: 'Menunggu', value: o.PENDING, color: 'var(--text-muted)' },
                  ]}
                />
              )}
            </Panel>
          </div>

          {ncrs.byDiscipline.length > 0 && (
            <Panel title="NCR terbuka per disiplin" note="Ke mana usaha mutu perlu diarahkan bulan ini." wide>
              <BarsH
                rows={ncrs.byDiscipline
                  .sort((a, b) => b.count - a.count)
                  .map((d) => ({ label: d.code, value: d.count }))}
              />
            </Panel>
          )}

          <Panel
            title="Menahan sertifikasi"
            note={
              data.blockingList.length === 0
                ? 'Tidak ada NCR yang sedang menahan uang.'
                : 'Selama NCR ini terbuka, baris BOQ pada aktivitasnya tidak akan disertifikasi.'
            }
            wide
          >
            {data.blockingList.length === 0 ? (
              <p className="hint">Semua aktivitas lolos gerbang mutu.</p>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>NCR</th>
                      <th>Judul</th>
                      <th>Keparahan</th>
                      <th>Aktivitas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.blockingList.map((n) => (
                      <tr key={n.id} onClick={() => nav(`/ncrs/${n.id}`)}>
                        <td className="num">{n.ncrNo}</td>
                        <td>{n.title}</td>
                        <td>
                          <span className="chip crit">{n.severity}</span>
                        </td>
                        <td>{n.activityCode ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </>
  );
}
